/** Animated tab selector for view modes (team variant) */

import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { VIEW_MODES, type ViewMode } from './types';

interface TeamViewModeTabsProps {
  activeMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  recentGamesCount: number;
  hasH2hData: boolean;
  opponentAbbr?: string;
}

export function TeamViewModeTabs({
  activeMode,
  onModeChange,
  recentGamesCount,
  hasH2hData,
  opponentAbbr,
}: TeamViewModeTabsProps): ReactElement {
  return (
    <div className="relative flex bg-theme-surface/80 rounded-lg p-0.5">
      {VIEW_MODES.map((mode) => {
        const gameCount = mode === 'L5' ? 5 : mode === 'L10' ? 10 : mode === 'L20' ? 20 : 0;
        const isDisabled = mode === 'H2H' ? !hasH2hData : recentGamesCount < gameCount;
        const isActive = activeMode === mode;
        return (
          <button
            type="button"
            key={mode}
            onClick={() => !isDisabled && onModeChange(mode)}
            disabled={isDisabled}
            className={cn(
              'relative px-2.5 py-1 text-xs font-semibold rounded-md transition-colors z-10',
              isActive
                ? 'text-theme-on-primary-fill'
                : isDisabled
                  ? 'text-theme-text-tertiary/40'
                  : 'text-theme-text-tertiary hover:text-theme-text-secondary',
            )}
            title={
              mode === 'H2H'
                ? hasH2hData
                  ? `vs ${opponentAbbr || 'opponent'}`
                  : 'No H2H data'
                : `Last ${gameCount} games`
            }
          >
            {isActive && (
              <motion.div
                layoutId="activeTeamViewTab"
                className="absolute inset-0 bg-theme-primary-fill rounded-md"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative">{mode}</span>
          </button>
        );
      })}
    </div>
  );
}
